import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plane, Plus, Check, X, Hourglass, CheckCircle2, XCircle, Users, ChevronLeft, ChevronRight, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

const DOW_ABBR = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];
function fmtDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return `${DOW_ABBR[dt.getDay()]} ${d}/${m}/${y}`;
}
function tomorrowIso() {
  const dt = new Date(); dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
}

const STATUS = {
  pendiente: { label: "Pendiente", cls: "border-amber-200 bg-amber-50 text-amber-700", Icon: Hourglass },
  aprobado: { label: "Aprobado", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", Icon: CheckCircle2 },
  rechazado: { label: "Rechazado", cls: "border-red-200 bg-red-50 text-red-700", Icon: XCircle },
};

export default function TeletrabajoTab() {
  const qc = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [showReq, setShowReq] = useState(false);

  const meQ = useQuery({ queryKey: ["rrhh-remote", year], queryFn: async () => (await api.get("/rrhh/remote", { params: { year } })).data });
  const me = meQ.data;
  const canApprove = !!me?.can_approve;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["rrhh-remote"] });
    qc.invalidateQueries({ queryKey: ["rrhh-remote-requests"] });
    qc.invalidateQueries({ queryKey: ["rrhh-remote-team"] });
    qc.invalidateQueries({ queryKey: ["rrhh-hybrid"] });
  };

  const reqMut = useMutation({
    mutationFn: async ({ date, note }) => (await api.post("/rrhh/remote", { date, note })).data,
    onSuccess: (r) => { invalidate(); setShowReq(false); toast.success("Solicitud enviada · pendiente de aprobación"); (r.warnings || []).forEach((w) => toast.warning(w)); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancelMut = useMutation({
    mutationFn: async (rid) => (await api.delete(`/rrhh/remote/${rid}`)).data,
    onSuccess: () => { invalidate(); toast.success("Solicitud cancelada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-6" data-testid="rrhh-teletrabajo">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Plane className="h-6 w-6 text-primary" />Teletrabajo voluntario</h1>
          <p className="text-sm text-muted-foreground">10 días/año · se solicitan con ≥1 día de antelación sobre días de oficina · los aprueban Administración o Marta.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y - 1)} data-testid="tt-year-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="px-2 text-sm font-semibold" data-testid="tt-year">{year}</span>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y + 1)} data-testid="tt-year-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button onClick={() => setShowReq(true)} data-testid="tt-new-btn"><Plus className="mr-1.5 h-4 w-4" />Solicitar teletrabajo</Button>
        </div>
      </div>

      {/* Contador (solo lo ve la persona y los gestores) */}
      {meQ.isLoading ? <Skeleton className="h-24 w-full" /> : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <CounterCard label="Cupo anual" value={me?.allowance ?? 10} testid="tt-allowance" />
          <CounterCard label="Aprobados" value={me?.approved ?? 0} tone="emerald" testid="tt-approved" />
          <CounterCard label="Pendientes" value={me?.pending ?? 0} tone="amber" testid="tt-pending" />
          <CounterCard label="Restantes" value={me?.remaining ?? 0} tone="blue" testid="tt-remaining" />
        </div>
      )}

      {/* Mis solicitudes */}
      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mis solicitudes {year}</h2>
        {meQ.isLoading ? <Skeleton className="h-20 w-full" /> : (me?.days || []).length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="tt-empty">Aún no has solicitado días de teletrabajo este año.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="tt-my-list">
            {me.days.map((r) => {
              const st = STATUS[r.status] || STATUS.pendiente;
              const canCancel = r.status === "pendiente" || (r.status === "aprobado" && r.date > (me.today || ""));
              return (
                <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3" data-testid={`tt-row-${r.id}`}>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className={st.cls}><st.Icon className="mr-1 h-3 w-3" />{st.label}</Badge>
                    <span className="text-sm font-medium">{fmtDate(r.date)}</span>
                    {r.note && <span className="text-xs text-muted-foreground">· {r.note}</span>}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {r.status === "aprobado" && r.approved_by && <span>Aprobado por {r.approved_by}{r.approver_message ? ` · “${r.approver_message}”` : ""}</span>}
                    {r.status === "rechazado" && <span className="text-red-600">{r.reject_reason || "Rechazado"}</span>}
                    {canCancel && <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => cancelMut.mutate(r.id)} data-testid={`tt-cancel-${r.id}`}><X className="mr-1 h-3.5 w-3.5" />Cancelar</Button>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {canApprove && <ApproverPanel year={year} onChange={invalidate} />}

      {showReq && <RequestDialog onClose={() => setShowReq(false)} onSave={(p) => reqMut.mutate(p)} saving={reqMut.isPending} />}
    </div>
  );
}

function CounterCard({ label, value, tone = "slate", testid }) {
  const tones = { slate: "text-foreground", emerald: "text-emerald-600", amber: "text-amber-600", blue: "text-blue-600" };
  return (
    <Card><CardContent className="p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-heading text-3xl font-bold ${tones[tone]}`} data-testid={testid}>{value}</p>
    </CardContent></Card>
  );
}

function RequestDialog({ onClose, onSave, saving }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tt-req-dialog">
        <DialogHeader>
          <DialogTitle>Solicitar teletrabajo voluntario</DialogTitle>
          <DialogDescription>Elige un día de oficina (martes o tu día elegido) con al menos 1 día de antelación. Lo aprueban Administración o Marta.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Día (martes o tu día elegido)</Label>
            <Input type="date" min={tomorrowIso()} value={date} onChange={(e) => setDate(e.target.value)} data-testid="tt-req-date" />
            <p className="text-[11px] text-muted-foreground">Con al menos 1 día de antelación. Solo sobre días de oficina.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Nota (opcional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo (opcional)" data-testid="tt-req-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => onSave({ date, note })} disabled={saving || !date} data-testid="tt-req-save">{saving ? "Enviando…" : "Enviar solicitud"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproverPanel({ year, onChange }) {
  const qc = useQueryClient();
  const [decision, setDecision] = useState(null); // { item, mode: 'approve'|'reject' }
  const reqsQ = useQuery({ queryKey: ["rrhh-remote-requests"], queryFn: async () => (await api.get("/rrhh/remote/requests", { params: { status: "pendiente" } })).data });
  const teamQ = useQuery({ queryKey: ["rrhh-remote-team", year], queryFn: async () => (await api.get("/rrhh/remote/team", { params: { year } })).data });

  const after = () => {
    qc.invalidateQueries({ queryKey: ["rrhh-remote-requests"] });
    qc.invalidateQueries({ queryKey: ["rrhh-remote-team"] });
    qc.invalidateQueries({ queryKey: ["rrhh-hybrid"] });
    onChange?.(); setDecision(null);
  };
  const approveMut = useMutation({
    mutationFn: async ({ rid, message }) => (await api.post(`/rrhh/remote/${rid}/approve`, { message })).data,
    onSuccess: () => { after(); toast.success("Solicitud aprobada · aviso enviado a la persona"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const rejectMut = useMutation({
    mutationFn: async ({ rid, reason }) => (await api.post(`/rrhh/remote/${rid}/reject`, { reason })).data,
    onSuccess: () => { after(); toast.success("Solicitud rechazada · aviso enviado a la persona"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const saving = approveMut.isPending || rejectMut.isPending;
  const confirmDecision = (message) => {
    if (!decision) return;
    if (decision.mode === "approve") approveMut.mutate({ rid: decision.item.id, message });
    else rejectMut.mutate({ rid: decision.item.id, reason: message });
  };

  const rows = reqsQ.data?.rows || [];

  return (
    <>
      <section data-testid="tt-approver">
        <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Hourglass className="h-3.5 w-3.5" />Solicitudes pendientes ({rows.length})</h2>
        {reqsQ.isLoading ? <Skeleton className="h-20 w-full" /> : rows.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="tt-no-pending">No hay solicitudes pendientes.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="tt-pending-list">
            {rows.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3" data-testid={`tt-pending-${r.id}`}>
                <div className="flex items-center gap-2.5">
                  <UserAvatar name={r.user?.name} color={r.user?.color} size="sm" />
                  <div>
                    <p className="text-sm font-medium">{r.user?.name}</p>
                    <p className="text-xs text-muted-foreground">{fmtDate(r.date)}{r.note ? ` · ${r.note}` : ""}</p>
                    {(r.warnings || []).length > 0 && (
                      <ul className="mt-1 space-y-0.5" data-testid={`tt-warn-${r.id}`}>
                        {r.warnings.map((w, i) => (
                          <li key={i} className="flex items-start gap-1 text-[11px] font-medium text-amber-600"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDecision({ item: r, mode: "reject" })} data-testid={`tt-reject-${r.id}`}><X className="mr-1 h-3.5 w-3.5" />Rechazar</Button>
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecision({ item: r, mode: "approve" })} data-testid={`tt-approve-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" />Aprobar</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section data-testid="tt-team-table">
        <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Users className="h-3.5 w-3.5" />Equipo · teletrabajo {year}</h2>
        {teamQ.isLoading ? <Skeleton className="h-40 w-full" /> : (
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="p-3">Persona</th><th className="p-3 text-center">Aprobados</th><th className="p-3 text-center">Pendientes</th><th className="p-3 text-center">Restantes</th></tr>
              </thead>
              <tbody className="divide-y">
                {(teamQ.data?.rows || []).map((u) => (
                  <tr key={u.id} data-testid={`tt-team-${u.id}`}>
                    <td className="p-3"><span className="flex items-center gap-2"><UserAvatar name={u.name} color={u.color} size="xs" />{u.name}</span></td>
                    <td className="p-3 text-center font-semibold text-emerald-600">{u.approved}</td>
                    <td className="p-3 text-center text-amber-600">{u.pending}</td>
                    <td className="p-3 text-center text-muted-foreground">{u.remaining}/{u.allowance}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </section>

      {decision && <DecisionDialog decision={decision} onClose={() => setDecision(null)} onConfirm={confirmDecision} saving={saving} />}
    </>
  );
}

function DecisionDialog({ decision, onClose, onConfirm, saving }) {
  const isApprove = decision.mode === "approve";
  const [message, setMessage] = useState("");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tt-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isApprove ? "Aprobar solicitud" : "Rechazar solicitud"}</DialogTitle>
          <DialogDescription>
            {isApprove
              ? "Puedes añadir un mensaje para la persona. Recibirá un email con tu decisión y tu aclaración."
              : "Indica el motivo. La persona recibirá un email con tu decisión y el motivo."}
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{decision.item?.user?.name} · {fmtDate(decision.item?.date)}</p>
        <div className="space-y-1.5">
          <Label>{isApprove ? "Mensaje / aclaración (opcional)" : "Motivo (opcional)"}</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
            placeholder={isApprove ? "Ej.: aprobado, recuerda dejar el panel cubierto." : "Motivo del rechazo"} data-testid="tt-decision-message" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className={isApprove ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
            onClick={() => onConfirm(message)} disabled={saving} data-testid="tt-decision-confirm">
            {saving ? "Enviando…" : (isApprove ? "Aprobar y enviar" : "Rechazar y enviar")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
